import { IShoppingBagData } from "../../types/ShoppingBag";
import { Box, Button, Divider, Typography } from "@mui/material";
import { Colors } from "../../helpers/Consts";
import LocationOn from "@mui/icons-material/LocationOn";
import { UserOrderStatus } from "../../types/UserOrder";
import { useRef, useEffect, useState } from "react";

type Props = {
  order: IShoppingBagData;
};

type MsgDetails = {
  message: string;
  color: string;
}

function IfWithdrawShowAddress({ order }: Props) {

  const isReady = order.status === UserOrderStatus.STATUS_PEDIDO_AGUARDANDO_RETIRADA;

  // aria-live only while ready so screen readers get notified once
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    if (isReady && !announced) setAnnounced(true);
    if (!isReady && announced) setAnnounced(false);
  }, [isReady, announced]);

  // respect prefers-reduced-motion
  const reduceMotion =
    typeof window !== "undefined" &&
    !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  function getMensagem() {
    let msgDetails: MsgDetails;

    switch (order.status) {
      /* estes status já foram tratados na função checaSeNaoDeveMostrarNada,
      mas eu vou tratar aqui também com uma mensagem:
          return order.status === UserOrderStatus.STATUS_PEDIDO_CANCELADO_LOJA
      || order.status === UserOrderStatus.STATUS_PEDIDO_CANCELADO_CLIENTE
      || order.status === UserOrderStatus.STATUS_PEDIDO_CONCLUIDO
      || order.status === UserOrderStatus.STATUS_PEDIDO_A_CAMINHO
      || order.status === UserOrderStatus.STATUS_PEDIDO_DEVOLVIDO
      || order.status === UserOrderStatus.STATUS_PEDIDO_ENTREGUE
      || order.status === UserOrderStatus.STATUS_PEDIDO_EXPIRADO
      || order.status === UserOrderStatus.STATUS_PEDIDO_CANCELADO_BOT;
      */

      case UserOrderStatus.STATUS_PEDIDO_ENVIADO:
      case UserOrderStatus.STATUS_PEDIDO_PENDENTE:
      case UserOrderStatus.STATUS_PEDIDO_PREPARACAO:
      case UserOrderStatus.STATUS_PEDIDO_PREPARADO:
        msgDetails = {
          message: "Quando seu pedido estiver pronto, venha retirar no nosso estabelecimento.",
          color: Colors.orangePrimary
        };
        break;


      case UserOrderStatus.STATUS_PEDIDO_AGUARDANDO_RETIRADA:
        msgDetails = {
          message: "Seu pedido está PRONTO.\nVenha buscar em nosso endereço:",
          color: Colors.successTextColor
        }
        break;

      // isto não deveria acontecer, eu acho:
      case UserOrderStatus.STATUS_PEDIDO_A_CAMINHO:
        msgDetails = {
          message: "Seu pedido está a caminho, aguarde que em breve ele chegará até você.",
          color: Colors.orangePrimary
        }
        break;

      case UserOrderStatus.STATUS_PEDIDO_EXPIRADO:
        msgDetails = {
          message: "Pedido expirado.",
          color: Colors.errorTextColor
        }
        break;

      case UserOrderStatus.STATUS_PEDIDO_SOLICITACAO_CANCELAMENTO:
        msgDetails = {
          message: "Processando solicitação de cancelamento.",
          color: Colors.errorTextColor
        };
        break;

      case UserOrderStatus.STATUS_PEDIDO_DEVOLVIDO:
      case UserOrderStatus.STATUS_PEDIDO_CANCELADO_LOJA:
      case UserOrderStatus.STATUS_PEDIDO_CANCELADO_CLIENTE:
      case UserOrderStatus.STATUS_PEDIDO_CANCELADO_BOT:
        msgDetails = {
          message: "Pedido cancelado.",
          color: Colors.errorTextColor
        }
        break

      case UserOrderStatus.STATUS_PEDIDO_ENTREGUE:
      case UserOrderStatus.STATUS_PEDIDO_CONCLUIDO:
        msgDetails = {
          message: "Pedido concluído. Aproveite sua refeição :)",
          color: Colors.orangePrimary
        }
        break

        default:
          msgDetails = {
            message: "Quando seu pedido estiver pronto, venha retirar no nosso estabelecimento.",
            color: Colors.orangePrimary
          }
          break
    }

    return (
      <>
        <Typography
          fontSize="0.7rem"
          color={msgDetails.color}
          sx={{
            flexBasis: { xs: "auto", md: "26%" },
            maxWidth: { md: "26%" },
            textAlign: { xs: "center", md: "left" },
            marginBottom: { xs: "10px", md: 0 },
            whiteSpace: 'pre-line'
          }}
        >
          {msgDetails.message}
        </Typography>
      </>
    )
  }

  function checaSeNaoDeveMostrarNada() {
    if(!order.withdraw) return true;

    // é withdraw, mas o status cancelado, ou entregue, etc, não mostramos nada:
    return order.status === UserOrderStatus.STATUS_PEDIDO_CANCELADO_LOJA
      || order.status === UserOrderStatus.STATUS_PEDIDO_CANCELADO_CLIENTE
      || order.status === UserOrderStatus.STATUS_PEDIDO_CONCLUIDO
      || order.status === UserOrderStatus.STATUS_PEDIDO_A_CAMINHO
      || order.status === UserOrderStatus.STATUS_PEDIDO_DEVOLVIDO
      || order.status === UserOrderStatus.STATUS_PEDIDO_ENTREGUE
      || order.status === UserOrderStatus.STATUS_PEDIDO_EXPIRADO
      || order.status === UserOrderStatus.STATUS_PEDIDO_CANCELADO_BOT;
  }

  function getLink() {
    const address = `${order.placeInfo.address_description} ${order.placeInfo.address_number}, ${order.placeInfo.address_neighborhood}, ${order.placeInfo.address_city} - ${order.placeInfo.address_state}`;
    const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

    return googleMapsLink;
  }

  // se não deve mostrar nada:
  if(checaSeNaoDeveMostrarNada()) return (<>

  </>)

  // Caso precisa mostrar:
  return (
  <>
    <Divider sx={{ padding: "5px" }} />
    <Box marginTop={"5px"} paddingTop={"10px"} paddingBottom={"10px"} display={"flex"}
         sx={{
           flexDirection: { xs: "column", md: "row" },
           alignItems: { xs: "stretch", md: "center" },
           justifyContent: { xs: "center", md: "space-evenly" },
           // apply single-shot pulse (then wait ~3s) when ready and motion is allowed
           ...(isReady && !reduceMotion
             ? {
               borderRadius: "8px",
               // keyframes: quick lift, hold ~0.5s, return, then rest until end of cycle
               "@keyframes readyPulseOnce": {
                 "0%": {
                   boxShadow: "0 0 0 0 rgba(0,0,0,0)",
                   transform: "translateY(0)",
                 },
                 // quick rise (within first ~8% of 3.6s -> ~0.29s)
                 "8%": {
                   boxShadow: `0 12px 28px 0 ${Colors.successTextColor}33`,
                   transform: "translateY(-6px)",
                 },
                 // hold at raised state until ~22% (~0.79s total, so ~0.5s hold after the rise)
                 "22%": {
                   boxShadow: `0 12px 28px 0 ${Colors.successTextColor}33`,
                   transform: "translateY(-6px)",
                 },
                 // return to normal (by ~30% of the cycle)
                 "30%": {
                   boxShadow: "0 0 0 0 rgba(0,0,0,0)",
                   transform: "translateY(0)",
                 },
                 // rest until next repetition
                 "100%": {
                   boxShadow: "0 0 0 0 rgba(0,0,0,0)",
                   transform: "translateY(0)",
                 },
               },
               // total cycle ~3.6s -> rise+hold+return ~0.6-0.8s, rest ~3s
               animation: "readyPulseOnce 3.6s cubic-bezier(.2,.8,.2,1) infinite",
               background:
                 "linear-gradient(90deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
             }
             : isReady && reduceMotion
               ? // if user prefers reduced motion, show a static emphasized style (no animation)
               {
                 borderRadius: "8px",
                 boxShadow: `0 8px 20px ${Colors.successTextColor}33`,
                 background:
                   "linear-gradient(90deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
               }
               : {})
         }}>
      {/* aria-live region (visually hidden) to notify assistive tech */}
      {announced && (
        <span style={{
          position: 'absolute',
          left: -9999,
          width: 1,
          height: 1,
          overflow: 'hidden'
        }} aria-live="assertive">
      Seu pedido está pronto para retirada.
    </span>
      )}
          {getMensagem()}

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              marginRight: "9px",
              marginLeft: "9px",
              display: { xs: "none", md: "block" },
            }}
          />

      {/* Container dos: container do endereço + botão mapa  */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: { xs: "center", md: "space-around" },
              width: { xs: "auto", md: "65%" },
              alignSelf: { xs: "center"}
            }}
          >
            {/* Container do endereço: */}
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              flex: { xs: 1, md: "initial" },
              marginTop: { xs: "0px", md: "0px" },

            }}>
              <Typography
                fontSize="0.6rem"
                color={Colors.baseTextColor}
              >
                Nosso Endereço:
              </Typography>
              <Typography
                fontSize="0.8rem"
                color={Colors.baseTextColor}
              >
                {order.placeInfo.address_description}, {order.placeInfo.address_number}
              </Typography>
              <Typography
                fontSize="0.8rem"
                color={Colors.baseTextColor}
              >
                {order.placeInfo.address_neighborhood}, {order.placeInfo.address_city} - {order.placeInfo.address_state}
              </Typography>

            </Box>
            <Divider orientation="vertical" flexItem sx={{marginRight: "9px", marginLeft: "9px"}} />
            <Button

              variant="outlined"
              startIcon={<LocationOn />}
              target="_blank"
              href={getLink()}
              sx={(theme) => {
                const reduceMotion = false
                  /*typeof window !== "undefined" &&
                  !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);*/

                return {
                  position: "relative",
                  overflow: "hidden",
                  zIndex: 0,
                  transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, color 0.18s ease",
                  // base color while ready
                  ...(isReady && {
                    borderColor: Colors.successTextColor,
                    color: Colors.successTextColor,
                    background: "transparent",
                  }),
                  // pseudo-element glint
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-75%",              // start off the left
                    height: "100%",
                    width: "50%",              // width of the glint strip
                    background:
                      "linear-gradient(120deg, rgba(128,44,0,0) 0%, rgba(256,88,0,0.36) 50%, rgba(255,255,255,0) 100%)",
                    transform: "skewX(-20deg)",
                    pointerEvents: "none",
                    // only animate when ready and motion not reduced
                    ...(isReady && !reduceMotion
                      ? {
                        animation: "glint-sweep 1.2s linear infinite",
                      }
                      : {
                        // keep it hidden when not animating
                        opacity: 0,
                      }),
                  },
                  // keyframes for the glint sweep
                  "@keyframes glint-sweep": {
                    "0%": { left: "-75%" },
                    "60%": { left: "125%" }, // sweep across
                    "100%": { left: "125%" },
                  },
                  // small emphasis on hover/focus (still smooth)
                  "&:hover, &:focus": {
                    transform: "translateY(-2px)",
                    boxShadow: isReady ? `0 8px 20px ${Colors.successTextColor}33` : undefined,
                  },
                };
              }}
            >
              Mapa
            </Button>
          </Box>
        </Box>
  </>)
}

export default IfWithdrawShowAddress